import { isDefectOverlayPresentation } from '../defectOverlays/overlayKinds';

export const SDM_DEFECT_FEATURE_CATEGORY = 'sdm-defect' as const;

export type SdmFeatureInfoPresentation =
  | 'map-vector'
  | 'crackmap'
  | 'pothole'
  | 'line-condition';

const HIDDEN_FEATURE_INFO_KEYS = new Set([
  'category',
  'geometry',
  'layerData',
  'attributes',
  'elements',
  'segmentable',
  'ObjectID',
  'OBJECTID',
  'FID',
  'geometryKind',
]);

const CRACKMAP_HIDDEN_FEATURE_INFO_KEYS = new Set([
  'objectid',
  'filename',
  'wkt',
  'latitude',
  'longitude',
]);

export type SdmFeatureInfoValue = string;

export interface SdmFeatureInfoDetails {
  readonly category: typeof SDM_DEFECT_FEATURE_CATEGORY;
  readonly name: string;
  readonly [key: string]: SdmFeatureInfoValue;
}

function isDisplayableValue(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}


const FEATURE_INFO_FIXED_DECIMAL_KEYS: Readonly<Record<string, number>> = {
  maxdepth: 2,
};

const FEATURE_INFO_NULL_SENTINEL = -9999;

const FEATURE_INFO_NULL_DISPLAY = 'N/A';

function isFeatureInfoNullSentinel(value: string | number | boolean): boolean {
  if (typeof value === 'number') {
    return value === FEATURE_INFO_NULL_SENTINEL;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return false;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) && numeric === FEATURE_INFO_NULL_SENTINEL;
  }
  return false;
}

function formatFeatureInfoValue(
  key: string,
  value: string | number | boolean,
): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (isFeatureInfoNullSentinel(value)) {
    return FEATURE_INFO_NULL_DISPLAY;
  }

  const decimals = FEATURE_INFO_FIXED_DECIMAL_KEYS[normalizeFeatureInfoKey(key)];
  if (decimals !== undefined) {
    const numeric =
      typeof value === 'number' ? value : Number(String(value).trim());
    if (Number.isFinite(numeric)) {
      return numeric.toFixed(decimals);
    }
  }

  return String(value);
}

export function isCrackmapWfsFeature(
  attributes: Readonly<Record<string, unknown>>,
): boolean {
  return 'sectionID' in attributes || 'crackID' in attributes;
}

function normalizeFeatureInfoKey(key: string): string {
  return key.toLowerCase();
}

function isHiddenFeatureInfoKey(
  key: string,
  presentation?: SdmFeatureInfoPresentation,
): boolean {
  const normalizedKey = normalizeFeatureInfoKey(key);

  for (const hidden of HIDDEN_FEATURE_INFO_KEYS) {
    if (normalizeFeatureInfoKey(hidden) === normalizedKey) {
      return true;
    }
  }

  if (presentation === 'crackmap') {
    return CRACKMAP_HIDDEN_FEATURE_INFO_KEYS.has(normalizedKey);
  }

  return false;
}

function isOverlayFeatureInfoPresentation(
  presentation: SdmFeatureInfoPresentation | undefined,
): boolean {
  return isDefectOverlayPresentation(presentation);
}

function resolveFeatureInfoPresentation(
  attributes: Readonly<Record<string, unknown>>,
  presentation?: SdmFeatureInfoPresentation,
): SdmFeatureInfoPresentation | undefined {
  if (presentation) {
    return presentation;
  }
  return isCrackmapWfsFeature(attributes) ? 'crackmap' : undefined;
}

export function buildSdmFeatureInfoDetails(
  attributes: Record<string, unknown>,
  options: {
    readonly layerTitle?: string;
    readonly latitude?: number;
    readonly longitude?: number;
    readonly presentation?: SdmFeatureInfoPresentation;
  } = {},
): SdmFeatureInfoDetails {
  const rows: Record<string, string> = {};
  const presentation = resolveFeatureInfoPresentation(attributes, options.presentation);
  const isOverlay = isOverlayFeatureInfoPresentation(presentation);

  for (const [key, value] of Object.entries(attributes)) {
    if (isHiddenFeatureInfoKey(key, presentation) || !isDisplayableValue(value)) {
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }
    rows[key] = formatFeatureInfoValue(key, value);
  }

  if (
    !isOverlay &&
    options.latitude != null &&
    Number.isFinite(options.latitude)
  ) {
    rows.latitude = String(options.latitude);
  }
  if (
    !isOverlay &&
    options.longitude != null &&
    Number.isFinite(options.longitude)
  ) {
    rows.longitude = String(options.longitude);
  }

  const name =
    options.layerTitle?.trim() ||
    (typeof attributes.section === 'string' && attributes.section.trim()
      ? attributes.section
      : null) ||
    (typeof attributes.measurement === 'string' && attributes.measurement.trim()
      ? attributes.measurement
      : null) ||
    'Selected feature';

  return {
    category: SDM_DEFECT_FEATURE_CATEGORY,
    name,
    ...rows,
  };
}

export function toSdmFeatureInfoRows(
  details: SdmFeatureInfoDetails,
): Record<string, string> {
  const { category: _category, ...rows } = details;
  return rows;
}

export function isSdmFeatureInfoDetails(
  value: unknown,
): value is SdmFeatureInfoDetails {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.category === SDM_DEFECT_FEATURE_CATEGORY &&
    typeof record.name === 'string' &&
    record.name.trim() !== ''
  );
}

export function shouldCloseAttributesForActiveLayer(
  sourceLayerId: string | null | undefined,
  activeLayers: ReadonlyArray<{ readonly id: string; readonly visible: boolean }>,
): boolean {
  if (sourceLayerId == null || sourceLayerId === '') {
    return false;
  }
  const layer = activeLayers.find((entry) => entry.id === sourceLayerId);
  return layer == null || !layer.visible;
}

